import { useState } from "react";
import Joi from "joi-browser";

import { useWhatsappAddPhoneNumber } from "V2.0/HomePages/Integrations/Whatsapp/models/useWhatsappAddPhoneNumber";

import { PhoneIcon, PlusIcon } from "@heroicons/react/24/solid";
import { message } from "antd";

import { Button } from "V2.0/common/button";
import { Dialog, DialogBody, DialogTitle } from "V2.0/common/dialog";
import Form from "V2.0/components/Form";
import { showErrorToast } from "V2.0/utils";
import TabsWithBadge from "V2.0/common/tabsWithBadge";
import {
  Table,
  TableHead,
  TableBody,
  TableHeader,
  TableRow,
  TableCell,
} from "V2.0/common/table";
import { Switch } from "V2.0/common/switch";
import { postEnableVoiceCalling } from "V2.0/services/whatsappService";

function PhoneNumberIdsTable(props) {
  const { phoneNumberIds, onEnableVoiceCall, voiceEnabledPhoneNumbers, disabled } = props;

  function handleVoiceCallEnable(phoneNumberId, enableStatus) {
    const payload = {
      phoneNumberId,
      status: enableStatus,
    };

    const enableVoiceCallLifecycleMessages = {
      loading: "Turing ON Voice Calls",
      success: "Voice Calls turned ON",
      error: "Failed to turn ON Voice Calls",
    };

    const disableVoiceCallLifecycleMessages = {
      loading: "Turning OFF Voice Calls",
      success: "Voice Calls turned OFF",
      error: "Failed to turn OFF Voice Calls",
    };

    const lifecycleMessages = enableStatus === "ENABLED"
      ? enableVoiceCallLifecycleMessages
      : disableVoiceCallLifecycleMessages;

    const lifecycleCbs = {
      onInit: () => {
        message.loading(lifecycleMessages.loading);
      },
      onSuccess: () => {
        message.destroy();
        message.success(lifecycleMessages.success);
      },
      onError: (e) => {
        message.destroy();
        showErrorToast(e, lifecycleMessages.error);
      },
    };

    onEnableVoiceCall(payload, lifecycleCbs);
  }

  return (
    <Table>
      <TableHead>
        <TableHeader>Number Id</TableHeader>
        <TableHeader>Calls</TableHeader>
      </TableHead>
      <TableBody>
        {phoneNumberIds.map((phoneNumberId) => {
          const { voiceCallingEnabled } =
            voiceEnabledPhoneNumbers[phoneNumberId] ?? false;
          const toggleVoiceCallStatus = voiceCallingEnabled
            ? "DISABLED"
            : "ENABLED";

          return (
            <TableRow key={phoneNumberId}>
              <TableCell>{phoneNumberId}</TableCell>
              <TableCell>
                <Switch
                  color="emerald"
                  checked={voiceCallingEnabled}
                  onClick={() => {
                    handleVoiceCallEnable(phoneNumberId, toggleVoiceCallStatus);
                  }}
                  disabled={disabled}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

export function WhatsappPhoneNumbers(props) {
  const {
    existingConfiguration,
    projectId,
    fetchWhatsappConfiguration,
    tabs,
    setActiveTab,
    disabled
  } = props;
  const { phoneNumberIds, phoneNumbersVoiceEnabled } = existingConfiguration;
  const [isOpen, setIsOpen] = useState(false);

  const { state, addPhoneNumber } = useWhatsappAddPhoneNumber();

  const voiceEnabledPhoneNumbers = (function () {
    if (!phoneNumbersVoiceEnabled) {
      return {};
    }

    return phoneNumbersVoiceEnabled?.reduce(function (
      acc,
      phoneNumberVoiceEnabled
    ) {
      const { id } = phoneNumberVoiceEnabled;

      return {
        ...acc,
        [id]: phoneNumberVoiceEnabled,
      };
    },
    {});
  })();

  const onSuccess = () => {
    message.destroy();
    message.success("Phone number added");
    fetchWhatsappConfiguration(projectId, false);
    setIsOpen(false);
  };

  const onError = (error) => {
    message.destroy();
    showErrorToast(error, "Failed to add phone number");
    console.error(error);
    setIsOpen(false);
  };

  const onInit = () => {
    message.loading("Adding phone number", 0.5);
  };

  const handleAdd = (data) => {
    addPhoneNumber({ projectId: projectId, data, onInit, onSuccess, onError });
  };

  const handleEnabeVoiceCalling = async function (payload, lifecycleCbs) {
    lifecycleCbs.onInit();
    try {
      const response = await postEnableVoiceCalling({
        workspaceId: projectId,
        payload,
      });

      if (response.status === 200) {
        lifecycleCbs.onSuccess();
        fetchWhatsappConfiguration(projectId, false);
      }
    } catch (e) {
      lifecycleCbs.onError(e);
    }
  };

  return (
    <>
      <div className="flex flex-col w-2/3 border rounded-lg h-full">
        <div className="flex-shrink-0 flex justify-between items-center px-4 bg-gray-50 rounded-t-lg w-full">
          <TabsWithBadge
            className="flex-shrink-0"
            tabs={tabs}
            onTabChange={(e, { tab, index }) => setActiveTab(tab.name)}
          />
          <Button
            color="white"
            disabled={disabled}
            onClick={() => setIsOpen(true)}
          >
            <PlusIcon />
            Add
          </Button>
        </div>
        <div className="flex-1 min-h-[600px] p-4 overflow-y-scroll">
          <PhoneNumberIdsTable
            phoneNumberIds={phoneNumberIds}
            onEnableVoiceCall={handleEnabeVoiceCalling}
            voiceEnabledPhoneNumbers={voiceEnabledPhoneNumbers}
            disabled={disabled}
          />
        </div>
      </div>
      {isOpen && (
        <Dialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
        >
          <DialogTitle>Add Phone Number</DialogTitle>
          <DialogBody>
            <Form
              key={JSON.stringify({ phoneNumberId: "" })}
              loading={state.loading}
              setLoading={() => {}}
              inputField={{ phoneNumberId: "" }}
              template={[
                {
                  name: "phoneNumberId",
                  label: "Phone Number ID",
                  type: "text",
                },
              ]}
              schema={{
                phoneNumberId: Joi.string().required().label("Phone Number ID"),
              }}
              buttonInfo={{
                buttons: "2",
                primaryLabel: "Add",
                secondaryLabel: "Cancel",
              }}
              onSubmit={handleAdd}
              handleSecondary={() => setIsOpen(false)}
            />
          </DialogBody>
        </Dialog>
      )}
    </>
  );
}
